import { useEffect, useState } from 'react';
import { RefreshCw, Eye } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import { Button } from '../../../components/ui/button';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import StatusChip from '../../../components/common/StatusChip';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import Drawer from '../../../components/common/Drawer';
import SourceCombobox from '../../../components/common/SourceCombobox';
import MasterChildTradeViewer from '../components/trade/MasterChildTradeViewer';
import ExecutionTimeline from '../components/trade/ExecutionTimeline';
import { mamTradeService, mamManagerService } from '../services/mamService';
import { formatCurrency, formatDate } from '../utils';
import { MOCK_EXECUTION_TIMELINE_EVENTS } from '../mock';
import type { MasterTrade, ChildTrade, MAMManager } from '../types';

export default function MAMTrades() {
  const [trades, setTrades] = useState<MasterTrade[]>([]);
  const [managers, setManagers] = useState<MAMManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>(
    'all'
  );
  const [selectedManagerFilter, setSelectedManagerFilter] =
    useState<string>('all');
  const [selectedTrade, setSelectedTrade] = useState<MasterTrade | null>(null);
  const [childTrades, setChildTrades] = useState<ChildTrade[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadTrades = async () => {
    try {
      setLoading(true);
      setError(null);
      const [allTrades, mgrRes] = await Promise.all([
        mamTradeService.getMasterTrades(
          statusFilter === 'all' ? undefined : statusFilter
        ),
        mamManagerService.getAll(),
      ]);
      setTrades(allTrades);
      setManagers(mgrRes);
    } catch {
      setError('Failed to fetch MAM trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrades();
  }, [statusFilter]);

  const handleInspectTrade = async (t: MasterTrade) => {
    setSelectedTrade(t);
    setDrawerOpen(true);
    try {
      const children = await mamTradeService.getChildTrades(t.id);
      setChildTrades(children);
    } catch {
      setChildTrades([]);
    }
  };

  const handleRetryChildTrade = async (childId: string) => {
    try {
      await mamTradeService.retryFailedTrade(childId);
      if (selectedTrade) {
        const updated = await mamTradeService.getChildTrades(selectedTrade.id);
        setChildTrades(updated);
      }
    } catch {
      // silently handle
    }
  };

  const filteredTrades = trades.filter(t => {
    if (
      selectedManagerFilter !== 'all' &&
      t.managerId !== selectedManagerFilter
    ) {
      return false;
    }
    return true;
  });

  const columns: DataTableColumn<MasterTrade>[] = [
    {
      id: 'ticket',
      header: 'Ticket / Symbol',
      sortable: true,
      accessor: t => (
        <div>
          <div className="flex items-center space-x-2">
            <span className="font-mono text-xs font-bold text-foreground">
              #{t.ticket}
            </span>
            <span
              className={`px-1.5 py-0.5 text-[9px] font-bold rounded uppercase ${
                t.direction === 'buy'
                  ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300'
                  : 'bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300'
              }`}
            >
              {t.direction}
            </span>
            <span className="font-bold text-foreground">{t.symbol}</span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Manager: {t.managerName}
          </p>
        </div>
      ),
    },
    {
      id: 'masterVolume',
      header: 'Volume',
      sortable: true,
      align: 'right',
      accessor: t => (
        <span className="font-mono font-bold text-foreground">
          {t.masterVolume} Lots
        </span>
      ),
    },
    {
      id: 'openPrice',
      header: 'Open / Close Price',
      sortable: true,
      align: 'right',
      accessor: t => (
        <div className="font-mono text-xs">
          <p>{t.openPrice}</p>
          <p className="text-[10px] text-muted-foreground">
            {t.status === 'closed'
              ? t.closePrice || '—'
              : t.currentPrice || '—'}
          </p>
        </div>
      ),
    },
    {
      id: 'profit',
      header: 'Profit / Loss',
      sortable: true,
      align: 'right',
      accessor: t => (
        <span
          className={`font-mono font-extrabold ${t.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
        >
          {formatCurrency(t.profit)}
        </span>
      ),
    },
    {
      id: 'childCount',
      header: 'Allocated Clients',
      sortable: true,
      align: 'center',
      accessor: t => (
        <span className="font-semibold text-xs text-foreground">
          {t.childCount} accounts
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: t => <StatusChip status={t.status} size="sm" />,
    },
    {
      id: 'openTime',
      header: 'Time',
      sortable: true,
      accessor: t => (
        <div className="text-[10px] text-muted-foreground">
          <p>Open: {formatDate(t.openTime)}</p>
          {t.closeTime && <p>Close: {formatDate(t.closeTime)}</p>}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      accessor: t => (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={e => {
            e.stopPropagation();
            handleInspectTrade(t);
          }}
        >
          <Eye className="w-3.5 h-3.5 mr-1" /> Inspect
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Trades" />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Trades" />
        <ErrorState message={error} onRetry={loadTrades} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Trade Activity & Monitoring"
        description="Live and historical master trade executions across all MAM allocation pools"
      >
        <Button variant="outline" size="sm" onClick={loadTrades}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-card border border-border rounded-xl shadow-sm">
        {/* Status Toggle Buttons */}
        <div className="flex items-center space-x-1 bg-muted p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              statusFilter === 'all'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All Trades ({trades.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('open')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              statusFilter === 'open'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Open Positions
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('closed')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
              statusFilter === 'closed'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Closed History
          </button>
        </div>

        {/* Manager Filter */}
        <div className="flex items-center space-x-2 w-full sm:w-64">
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            Filter Manager:
          </span>
          <SourceCombobox
            items={[
              { value: 'all', label: 'All Managers' },
              ...managers.map(m => ({ value: m.id, label: m.name })),
            ]}
            value={selectedManagerFilter}
            onChange={val => setSelectedManagerFilter(val)}
          />
        </div>
      </div>

      {/* Main Trades DataTable */}
      <DataTable
        columns={columns}
        data={filteredTrades}
        searchKeys={['ticket', 'symbol', 'managerName', 'direction']}
        searchPlaceholder="Search trades by ticket #, symbol, manager..."
        onRowClick={t => handleInspectTrade(t)}
        initialPageSize={8}
      />

      {/* Trade Inspection Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={
          selectedTrade
            ? `Master Order #${selectedTrade.ticket} (${selectedTrade.symbol})`
            : 'Trade Details'
        }
        subtitle={selectedTrade ? `Manager: ${selectedTrade.managerName}` : ''}
      >
        {selectedTrade && (
          <div className="space-y-6 text-xs">
            <MasterChildTradeViewer
              masterTrade={selectedTrade}
              childTrades={childTrades}
              onRetryChildTrade={handleRetryChildTrade}
            />

            <div className="pt-4 border-t border-border">
              <ExecutionTimeline events={MOCK_EXECUTION_TIMELINE_EVENTS} />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
